import { useState } from 'react'
import { apiClient } from '../../services/api'
import Button from '../../components/ui/Button'
import { ui } from '../../styles/ui'

const DEEPSEEK_DEFAULTS = {
  id: 'deepseek-flash', name: 'DeepSeek Flash', provider: 'deepseek', vendor: 'deepseek',
  transport: 'openai_chat_completions', active: false, api_url: 'https://api.deepseek.com',
  credentialRef: 'DEEPSEEK_API_KEY', model: 'deepseek-flash', max_tokens: 4096,
  temperature: 0, thinking: false, structuredOutputMode: 'native_json', maxConcurrentRequests: 1, queueWaitTimeoutMs: 600000,
  capabilities: { text: true, vision: true, thinking: true, chatCompletions: true, responses: true },
}

export default function ProviderEngineEditor({ engines, onChange, saved }) {
  const [testing, setTesting] = useState('')
  const [testResult, setTestResult] = useState('')
  const update = (index, field, value) => onChange(engines.map((engine, current) => current === index ? { ...engine, [field]: value } : engine))
  const updateProvider = (index, value) => onChange(engines.map((engine, current) => current === index ? { ...engine, vendor: value, provider: value } : engine))
  const addDeepSeek = () => {
    if (!engines.some((engine) => engine.id === DEEPSEEK_DEFAULTS.id)) onChange([...engines, { ...DEEPSEEK_DEFAULTS }])
  }
  const testConnection = async (engine) => {
    setTesting(engine.id)
    setTestResult('')
    try {
      const response = await apiClient.post('/llm-settings/engine-health', { engine: { id: engine.id } })
      setTestResult(response.data.healthy ? `${engine.name || engine.id}: connection passed` : `${engine.name || engine.id}: ${response.data.error || 'connection failed'}`)
    } catch (error) {
      setTestResult(error.message || 'Provider test failed')
    } finally {
      setTesting('')
    }
  }
  const fields = [
    ['id', 'Engine ID'], ['name', 'Display name'], ['model', 'Model'], ['api_url', 'API base URL'],
    ['credentialRef', 'Environment secret reference'], ['maxConcurrentRequests', 'Max concurrent requests'],
    ['queueWaitTimeoutMs', 'Queue wait timeout (ms)'], ['max_tokens', 'Max output tokens'], ['temperature', 'Temperature'],
  ]
  return <section className={ui.card}>
    <div className={ui.cardBody}>
      <div className="flex items-center justify-between gap-3">
        <div><h2 className="text-lg font-semibold text-foreground">LLM providers</h2><p className={ui.subtitle}>Provider credentials are write-only and resolved by the backend.</p></div>
        <Button type="button" onClick={addDeepSeek}>Add DeepSeek Flash</Button>
      </div>
      {engines.map((engine, index) => <div key={`${engine.id}-${index}`} className="mt-5 rounded border p-4">
        <div className="mb-3 flex items-center justify-between">
          <strong>{engine.name || engine.id || `Engine ${index + 1}`}</strong>
          <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={Boolean(engine.active)} onChange={(event) => update(index, 'active', event.target.checked)} /> Active</label>
        </div>
        <div className="grid gap-3 md:grid-cols-2">
          <label className={ui.label}>Provider<select className={'mt-1 ' + ui.select} value={engine.vendor || engine.provider || 'openai'} onChange={(event) => updateProvider(index, event.target.value)}>
            <option value="openai">OpenAI</option><option value="deepseek">DeepSeek</option><option value="openai-compatible">Other OpenAI-compatible</option>
          </select></label>
          <label className={ui.label}>Transport<select className={'mt-1 ' + ui.select} value={engine.transport || 'openai_chat_completions'} onChange={(event) => update(index, 'transport', event.target.value)}>
            <option value="openai_chat_completions">Chat Completions</option><option value="openai_responses">Responses</option>
          </select></label>
          <label className={ui.label}>Structured output mode<select className={'mt-1 ' + ui.select}
            value={engine.structuredOutputMode || ''} onChange={(event) => update(index, 'structuredOutputMode', event.target.value || undefined)}>
            <option value="">Legacy default (native JSON)</option>
            <option value="native_json">Native JSON</option>
            <option value="prompt_only">Prompt only</option>
          </select></label>
          <div className="grid grid-cols-2 gap-2 rounded border p-3" aria-label="Engine capabilities">
            {['text', 'vision', 'thinking', 'structuredOutput', 'chatCompletions', 'responses'].map((capability) => <label key={capability} className="flex items-center gap-2 text-sm">
              <input type="checkbox" checked={engine.capabilities?.[capability] !== false} onChange={(event) => update(index, 'capabilities', { ...engine.capabilities, [capability]: event.target.checked })} /> {capability}
            </label>)}
          </div>
          {fields.map(([field, label]) => <label key={field} className={ui.label}>{label}
            <input className={'mt-1 ' + ui.input} type={['temperature', 'maxConcurrentRequests', 'queueWaitTimeoutMs', 'max_tokens'].includes(field) ? 'number' : 'text'}
              value={engine[field] ?? ''} onChange={(event) => update(index, field, ['temperature', 'maxConcurrentRequests', 'queueWaitTimeoutMs', 'max_tokens'].includes(field) ? Number(event.target.value) : event.target.value)} />
          </label>)}
          <label className={ui.label}>Infrastructure accounting profile ID
            <input className={'mt-1 ' + ui.input} value={engine.accountingProfileId ?? ''}
              onChange={(event) => update(index, 'accountingProfileId', event.target.value || undefined)} />
          </label>
          <p className="self-center text-xs text-muted-foreground">Profile rates and version are configured server-side in LLM_ACCOUNTING_PROFILES_JSON.</p>
          <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={Boolean(engine.thinking)} onChange={(event) => update(index, 'thinking', event.target.checked)} /> Enable reasoning/thinking</label>
          <p className="self-center text-xs text-muted-foreground">{engine.credentialConfigured ? 'Credential configured on server.' : `Set ${engine.credentialRef || 'provider credential'} in server secret configuration.`}</p>
        </div>
        <div className="mt-3 flex justify-end"><Button type="button" variant="ghost" disabled={!saved || testing === engine.id} onClick={() => testConnection(engine)}>
          {testing === engine.id ? 'Testing...' : saved ? 'Test connection' : 'Save before testing'}
        </Button></div>
      </div>)}
      {testResult && <p role="status" className="mt-3 text-sm">{testResult}</p>}
    </div>
  </section>
}
